import { Button, Dialog, DialogClose } from "@hellocarbo/lasagne";
import { use } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { PortalContainerContext, UserContext } from "@/contexts";
import useOutboundQueryParams from "@/hooks/useOutboundQueryParams";
import useSegmentEvent from "@/hooks/useSegmentEvent";
import { getYoutubeId } from "@/utils/youtubeLink";

interface UpsellModalProps {
  children: React.ReactNode;
  isDisabled: boolean;
}

const UpsellModal = ({ children, isDisabled }: UpsellModalProps) => {
  const { user } = use(UserContext);
  const { container } = use(PortalContainerContext);
  const { t } = useTranslation("simulation");
  const { sendSegmentEvent } = useSegmentEvent();
  const freetrial_params = useOutboundQueryParams();
  const isStartSubscriptionType = user?.company.subscriptionType === "start";
  const bookDemoUrl = `https://www.hellocarbo.com/reservez-votre-demo${freetrial_params}`;
  const optionsUrl = "/options/subscription?option=simulation";

  const onTriggerClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const target = e.target as HTMLElement;
    if (
      target.nodeName === "BUTTON" &&
      target.classList.contains("upsell-modal-trigger")
    ) {
      // If the click is on _this_ button, don't prevent default so the modal can open
      return;
    }
    e.preventDefault(); // Prevent the default behavior of the children (links / selects)
    target.parentElement?.closest("button")?.click();
    return;
  };

  if (user?.policies?.simulation["create"]) {
    return children;
  }

  const youtubeUrl = "https://www.youtube.com/watch?v=nCCXz2d8jTY";

  return (
    <Dialog
      trigger={
        <button
          className="upsell-modal-trigger"
          onClick={onTriggerClick}
          disabled={isDisabled}
        >
          {children}
        </button>
      }
      title={t("modal.title")}
      description={
        isStartSubscriptionType
          ? t("modal.startContent")
          : t("modal.optionContent")
      }
      warningMessage={
        isStartSubscriptionType
          ? t("modal.startWarning")
          : t("modal.optionWarning")
      }
      portalContainer={container as Element}
      footer={
        <>
          {isStartSubscriptionType && (
            <DialogClose asChild>
              <Button
                as={Link}
                to="/pricing"
                shape="link"
                onClick={() =>
                  sendSegmentEvent("Discover the plans", {
                    size: `${user?.company.employees}`,
                  })
                }
              >
                {t("modal.secondaryCta")}
              </Button>
            </DialogClose>
          )}
          <DialogClose asChild>
            <Button
              as="a"
              href={isStartSubscriptionType ? bookDemoUrl : optionsUrl}
              shouldOpenNewTab={isStartSubscriptionType}
              shape="solid"
              onClick={() =>
                sendSegmentEvent(
                  isStartSubscriptionType
                    ? "Subscription Asked"
                    : "option_subscription",
                  {
                    size: `${user?.company.employees}`,
                  }
                )
              }
            >
              {isStartSubscriptionType
                ? t("modal.primaryCta")
                : t("modal.paidAccountCta")}
            </Button>
          </DialogClose>
        </>
      }
    >
      {!isStartSubscriptionType && (
        <div className="my-md">
          <iframe
            id="player"
            width="100%"
            height="290px"
            className="animate-in fade-in rounded-md duration-1000"
            src={`https://www.youtube.com/embed/${getYoutubeId(youtubeUrl)}?enablejsapi=1&origin=${window.location.origin}`}
          />
        </div>
      )}
    </Dialog>
  );
};

export default UpsellModal;
